import { useEffect, useRef, useCallback, useMemo } from 'react';
import { TViewDefinition } from '../utils/view-resolver-utils';
import { useRendererCallbackMap } from './use-renderer-callback-map';

/**
 * Triggers analytics callbacks (onLoad/onView) for SSR-rendered components after hydration.
 */
export const useSSRAnalyticsCallbacks = (
  initialData: TViewDefinition | undefined,
  rendererCbMap: ReturnType<typeof useRendererCallbackMap>
) => {
  const hasTriggeredLoadRef = useRef(false);
  const hasSetupObserverRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const viewedComponentsRef = useRef<Set<string>>(new Set());

  const mainContentComponents = useMemo(() => {
    const layoutSections = initialData?.layout?.sections as Record<string, any> | undefined;
    return layoutSections?.mainContentCollection?.components || [];
  }, [initialData]);

  const triggerCallback = useCallback(
    (component: any, callbackName: 'onLoad' | 'onView') => {
      const callbacks = rendererCbMap[component?.componentId as keyof typeof rendererCbMap];
      const callback = callbacks?.[callbackName as keyof typeof callbacks];
      if (typeof callback === 'function') {
        (callback as (ref: unknown, data: unknown) => void)(null, component);
      }
    },
    [rendererCbMap]
  );

  const setupObserver = useCallback(() => {
    if (hasSetupObserverRef.current || !observerRef.current) return;

    const mainContent =
      document.querySelector('[data-testid="main-page"]') ||
      document.querySelector('[data-testid="main-content-collection"]') ||
      document.querySelector('main') ||
      document.querySelector('#main-content') ||
      document.body;

    if (!mainContent) return;

    const allSections = Array.from(mainContent.querySelectorAll('section'));
    const topLevelSections = allSections.filter(
      (section) => !allSections.some((other) => other !== section && other.contains(section))
    );

    const sectionsToObserve = topLevelSections.slice(0, mainContentComponents.length);
    if (sectionsToObserve.length === 0) return;

    hasSetupObserverRef.current = true;
    sectionsToObserve.forEach((section, index) => {
      (section as HTMLElement).dataset.componentIndex = String(index);
      observerRef.current?.observe(section);
    });
  }, [mainContentComponents.length]);

  useEffect(() => {
    if (!initialData) return;

    // Trigger onLoad callbacks once after hydration
    if (!hasTriggeredLoadRef.current && mainContentComponents.length > 0) {
      hasTriggeredLoadRef.current = true;
      mainContentComponents.forEach((component: any) => triggerCallback(component, 'onLoad'));
    }

    // Set up IntersectionObserver for onView callbacks
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            const componentIndex = (entry.target as HTMLElement).dataset.componentIndex;
            if (!componentIndex || viewedComponentsRef.current.has(componentIndex)) return;

            viewedComponentsRef.current.add(componentIndex);
            const component = mainContentComponents[parseInt(componentIndex, 10)];
            if (component) triggerCallback(component, 'onView');
          });
        },
        { threshold: 0.1, rootMargin: '50px 0px' }
      );
    }

    setupObserver();
    const rafId = requestAnimationFrame(setupObserver);
    const timeoutId = setTimeout(setupObserver, 500);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      observerRef.current?.disconnect();
    };
  }, [initialData, mainContentComponents, setupObserver, triggerCallback]);
};

export default useSSRAnalyticsCallbacks;
